import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatTableDataSource } from "@angular/material/table";
import { interval, Subscription } from "rxjs";
import { ObjectStatus } from "../../object-status";
import { MatSort } from "@angular/material/sort";
import { ApiService } from "../../services/api.service";
import { LoadService } from "../../services/load.service";
import { ShareFilesComponent } from "../share-files/share-files.component";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: 'app-uploads-status',
  templateUrl: './uploads-status.component.html',
  styleUrls: ['./uploads-status.component.css']
})
export class UploadsStatusComponent implements OnInit, OnDestroy, AfterViewInit {

  status: ObjectStatus[] = [];
  // @ts-ignore
  @ViewChild(MatSort) sort: MatSort;
  displayedColumns: string[] = [
    "checkbox",
    "file_name",
    "file_size",
    // "upload_state",
    "upload_date",
    "deletion_date",
    "malware_scan_status",
    "buttons"
  ];
  dataSource = new MatTableDataSource<ObjectStatus>();

  flag_loader = true;
  filter = false;

  statusSubscription: Subscription | null = null;
  uploadProgress: number | null = null;
  currentUploadObjectKey: string | null = null;
  localObjectStateMap: any = {};
  uploadList: ObjectStatus[] = [];
  @Output() shareFileListEvent = new EventEmitter<string[]>();
  shareFileList: any[] = []
  private refreshInterval: Subscription | null = null;

  constructor(
    public dialog: MatDialog,
    private apiService: ApiService,
    private loadService: LoadService,
    private changeDetectorRefs: ChangeDetectorRef
  ) {
  }

  openShareDialog(file: ObjectStatus) {
    const dialogRef = this.dialog.open(ShareFilesComponent, {
      width: '600px',
      data: [file],
      autoFocus: false
    });
    dialogRef.afterClosed().subscribe(result => {
      this.loadStatus();
      // Unset all checkboxes by clearing the selection
      this.shareFileList = [];
      this.shareFileListEvent.emit(this.shareFileList);
    });
  }

  isFileShared(element: any): boolean {
    return Array.isArray(this.shareFileList) && this.shareFileList.some(f => f && f.object_key === element.object_key);
  }

  ngOnInit(): void {
    this.loadStatus();
    this.dataSource.filter = "REMOVED";
    this.dataSource.filterPredicate =
      (data: ObjectStatus, filter: string) => !filter || data.upload_state !== filter;
    this.setupAutoRefresh();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    if (this.statusSubscription) this.statusSubscription.unsubscribe();
    if (this.refreshInterval) this.refreshInterval.unsubscribe();
  }

  getUploadProgressText(objectKey: string): string {
    const state = this.localObjectStateMap[objectKey];
    if (!state || !state.uploadedBytes || !state.totalBytes) {
      return `${state?.progress || 0}%`;
    }
    
    const uploaded = this.formatBytes(state.uploadedBytes);
    const total = this.formatBytes(state.totalBytes);
    return `${state.progress}% - ${uploaded} / ${total}`;
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1000; // Use 1000 for decimal (GB) instead of 1024 (GiB)
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  toggleRemovedObject() {
    this.filter = !this.filter
    if (this.filter) this.dataSource.filter = "";
    else this.dataSource.filter = "REMOVED"
  }

  addToUploadList(objectStatus: ObjectStatus) {
    this.uploadList.push(objectStatus)
    this.dataSource.data = this.status.concat(this.uploadList);
    this.changeDetectorRefs.detectChanges(); // Force change detection
  }

  removeFromUploadList(objectKey: string) {
    const index = this.uploadList.findIndex((element) => element.object_key == objectKey);
    if (index != -1) {
      this.uploadList.splice(index, 1);
      delete this.localObjectStateMap[objectKey];
    }
    this.dataSource.data = this.status.concat(this.uploadList);
  }

  checkDeleting(objectKey: string) {
    return this.localObjectStateMap[objectKey]
      && this.localObjectStateMap[objectKey].state
      && this.localObjectStateMap[objectKey].state == 'DELETING'
  }

  removeDeletingStatus(objectKey: string) {
    if (this.checkDeleting(objectKey)) {
      delete this.localObjectStateMap[objectKey];
    }
  }

  updateDataSource(result: any) {
    this.flag_loader = false;
    // Update status array in place to preserve object references
    const newStatus: ObjectStatus[] = [];
    for (const newItem of result) {
      const idx = this.status.findIndex(item => item.object_key === newItem.object_key);
      if (idx > -1) {
        // Update the existing object in place
        Object.assign(this.status[idx], newItem);
        newStatus.push(this.status[idx]);
      } else {
        newStatus.push(newItem);
      }
    }
    // Remove items not in result
    this.status = newStatus;
    this.status.forEach((element) => this.removeFromUploadList(element.object_key));
    this.dataSource.data = this.status.concat(this.uploadList);
    // Preserve selected files if they still exist
    if (this.shareFileList && this.shareFileList.length > 0) {
      const validKeys = new Set(this.status.map(f => f.object_key));
      this.shareFileList = this.shareFileList.filter(f => validKeys.has(f.object_key));
      this.shareFileListEvent.emit(this.shareFileList);
    }
    this.changeDetectorRefs.detectChanges();
    this.setupAutoRefresh();
  }

  setupAutoRefresh() {
    // If any file is in scan IN_PROGRESS, start interval, else clear it
    const hasInProgress = this.status.some(
      (file) => file.malware_scan_status === "IN_PROGRESS"
    );
    
    // Clear existing interval before creating new one to prevent multiple intervals
    if (this.refreshInterval) {
      this.refreshInterval.unsubscribe();
      this.refreshInterval = null;
    }
    
    // Start new interval only if there are files in progress
    if (hasInProgress) {
      this.refreshInterval = interval(2000).subscribe(() => this.loadStatus());
    }
  }

  loadStatus() {
    this.statusSubscription = this.apiService.getUploadedFiles().subscribe(
      (result: ObjectStatus[]) => {
        const filteredResult = result.filter(
          (file) => file.upload_state === "SUCCESS" || file.upload_state === "REMOVED"
        );
        this.updateDataSource(filteredResult);
        // Do NOT clear shareFileList here; updateDataSource will handle it
        // this.shareFileList = [];
        // this.shareFileListEvent.emit(this.shareFileList)

      },
      (error: any) => {
        console.log(error);
      }
    );
  }

  download(objectKey: string, fileName: string): void {
    this.apiService.getDownloadUrl(objectKey).subscribe((response: any) => {
      this.loadService
        .download(response.download_url, fileName);
    })
  }

  delete(file: ObjectStatus) {
    this.removeFileInList(file);
    const objectKey = file.object_key
    this.localObjectStateMap[objectKey] = {};
    this.localObjectStateMap[objectKey].state = "DELETING"
    this.apiService.deleteFile(objectKey).subscribe((response: any) => {
      this.statusSubscription = this.apiService.getUploadedFiles().subscribe(
        (result: ObjectStatus[]) => {
          const filteredResult = result.filter(
            (file) => file.upload_state === "SUCCESS" || file.upload_state === "REMOVED"
          );
          this.updateDataSource(filteredResult);
          this.updateDataSource(filteredResult);
          (<NodeListOf<HTMLInputElement>>document.getElementsByName("checkbox_file")).forEach(
            elt => elt.checked = false
          );
          delete this.localObjectStateMap[objectKey];
          this.shareFileList = [];
          this.shareFileListEvent.emit(this.shareFileList)
        },
        (error: any) => {
          console.log(error);
        }
      );
    })
  }

  cancel(objectKey: any) {
    const uploadState = this.localObjectStateMap[objectKey];
    if (!uploadState) return;
    
    // Handle single part upload
    if (uploadState.subscription) {
      uploadState.subscription.unsubscribe();
    }
    
    // Handle multipart upload - cancel all part subscriptions
    if (uploadState.subscriptions && Array.isArray(uploadState.subscriptions)) {
      uploadState.subscriptions.forEach((sub: any) => {
        if (sub && !sub.closed) {
          sub.unsubscribe();
        }
      });
    }
    
    // Remove from upload list
    this.removeFromUploadList(objectKey);
  }

  toggleCheckBox(file: ObjectStatus) {
    let checkbox = (<HTMLInputElement>document.getElementById(file.object_key))
    checkbox.checked = !checkbox.checked
    this.updateList(file)
  }

  addFileInList(file: ObjectStatus) {
    if (this.shareFileList.length < 25) this.shareFileList.push(file)
  }

  removeFileInList(file: ObjectStatus) {
    const index = this.shareFileList.indexOf(file)
    if (index > -1) {
      this.shareFileList.splice(index, 1);
    }
  }

  updateList(file: ObjectStatus) {
    let checkbox = (<HTMLInputElement>document.getElementById(file.object_key))
    if (checkbox.checked) this.addFileInList(file)
    else this.removeFileInList(file)
    this.shareFileListEvent.emit(this.shareFileList)
  }

  trackByObjectKey(index: number, item: ObjectStatus): string {
    return item.object_key;
  }
}
